/**
 * AppContext - Composed application context
 * 
 * This combines all modular contexts into a single provider and hook
 * for convenient access to all application state.
 * 
 * Individual contexts:
 * - FileContext: File data, parsers, waypoints, features, nodes
 * - SelectionContext: Selection state, multi-select
 * - UIContext: Status messages, loading state, modals
 * - VisualizerContext: 3D visualizer instance, server availability
 * - RobotContext: Robot connection, client instance
 */
import { createContext, useContext, useCallback } from 'react';
import { FileProvider, useFile } from './FileContext.jsx';
import { SelectionProvider, useSelection } from './SelectionContext.jsx';
import { UIProvider, useUI } from './UIContext.jsx';
import { VisualizerProvider, useVisualizer } from './VisualizerContext.jsx';
import { RobotProvider, useRobot } from './RobotContext.jsx';
import { convertWaypointsToWorldCoords } from '../utils/coordinateTransforms.js';

// Re-export individual hooks for direct access
export { useFile } from './FileContext.jsx';
export { useSelection } from './SelectionContext.jsx';
export { useUI } from './UIContext.jsx';
export { useVisualizer } from './VisualizerContext.jsx';
export { useRobot } from './RobotContext.jsx';

/**
 * useApp - Combined hook that provides access to all contexts
 * 
 * This hook combines all individual context hooks and adds
 * cross-context operations like reorderWaypointsInNode and reorderNodes.
 */
export const useApp = () => {
  const file = useFile();
  const selection = useSelection();
  const ui = useUI();
  const visualizer = useVisualizer();
  const robot = useRobot();

  // Cross-context operations that need access to multiple contexts
  const reorderWaypointsInNode = useCallback((nodeIndex, reorderedIndices) => {
    const { nodes, currentWaypoints, renumberWaypoints, setCurrentWaypoints, setNodes, parser } = file;
    const { visualizer: vis } = visualizer;
    const { currentFeatures } = file;

    // Capture the original types BEFORE modifying the nodes
    const originalNode = nodes[nodeIndex];
    const originalTypes = originalNode.indices.map(idx => currentWaypoints[idx]?.processType);

    const newNodes = [...nodes];
    newNodes[nodeIndex] = {
      ...newNodes[nodeIndex],
      indices: reorderedIndices,
      _originalTypes: originalTypes
    };

    // Renumber and reorder waypoints based on new order
    const { waypoints: reorderedWaypoints, nodes: updatedNodes } = renumberWaypoints(newNodes, currentWaypoints);

    setCurrentWaypoints(reorderedWaypoints);
    setNodes(updatedNodes);

    // Update the XML DOM to reflect reordering
    if (parser && parser.reorderWaypointsInXML) {
      parser.reorderWaypointsInXML(reorderedWaypoints);
    }

    // Update visualizer if available
    if (vis && currentFeatures.length > 0) {
      const waypointsForVis = convertWaypointsToWorldCoords(reorderedWaypoints, currentFeatures);
      vis.visualizeWaypoints(waypointsForVis, false);
    }
  }, [file, visualizer]);

  const reorderNodes = useCallback((reorderedNodes) => {
    const { currentWaypoints, renumberWaypoints, setCurrentWaypoints, setNodes, parser, currentFeatures } = file;
    const { visualizer: vis } = visualizer;

    // Renumber and reorder waypoints based on new node order
    const { waypoints: reorderedWaypoints, nodes: updatedNodes } = renumberWaypoints(reorderedNodes, currentWaypoints);

    setCurrentWaypoints(reorderedWaypoints);
    setNodes(updatedNodes);

    // Update the XML DOM to reflect reordering
    if (parser && parser.reorderWaypointsInXML) {
      parser.reorderWaypointsInXML(reorderedWaypoints);
    }

    // Update visualizer if available
    if (vis && currentFeatures.length > 0) {
      const waypointsForVis = convertWaypointsToWorldCoords(reorderedWaypoints, currentFeatures);
      vis.visualizeWaypoints(waypointsForVis, false);
    }
  }, [file, visualizer]);

  return {
    // File context
    ...file,
    
    // Selection context
    ...selection,
    
    // UI context
    ...ui,
    
    // Visualizer context
    ...visualizer,
    
    // Robot context
    ...robot,
    
    // Cross-context operations
    reorderWaypointsInNode,
    reorderNodes,
  };
};

/**
 * AppProvider - Composed provider that wraps all individual context providers
 */
export const AppProvider = ({ children }) => {
  return (
    <FileProvider>
      <SelectionProvider>
        <UIProvider>
          <VisualizerProvider>
            <RobotProviderWithServerCheck>
              {children}
            </RobotProviderWithServerCheck>
          </VisualizerProvider>
        </UIProvider>
      </SelectionProvider>
    </FileProvider>
  );
};

/**
 * RobotProviderWithServerCheck - Wrapper that connects RobotProvider to VisualizerContext
 * for server availability updates
 */
const RobotProviderWithServerCheck = ({ children }) => {
  const { setIsServerAvailable } = useVisualizer();
  
  return (
    <RobotProvider onServerAvailabilityChange={setIsServerAvailable}>
      {children}
    </RobotProvider>
  );
};


